import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, ArrowRight, Plus, Minus, Maximize2, X, Check } from "lucide-react";
import Chapter from "./Chapter";
import { Reveal } from "./Reveal";
import { Flag } from "./Flagify";
import { MARKETS, CONTACT } from "../data/content";
import { useMarkets } from "../data/cms";
import { useLang, MAP_CODE_TO_LANG } from "../i18n";

const GEO_URL = "/assets/world-110m.json";
const CENTER = [22, 47];
const clampZoom = (z) => Math.min(4, Math.max(1, z));

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const on = (e) => setMobile(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return mobile;
};

const btn = "inline-flex min-h-[48px] items-center justify-center gap-2 px-4 text-sm font-semibold transition-colors";

// Large, faint flag of the selected country — sits behind the map and the panel.
// Sizing is inline because flag-icons' `.fi` class hard-codes em-based dimensions.
const FlagWatermark = ({ code, width, height, className = "", opacity = 0.16, mask }) => (
  <span
    aria-hidden="true"
    data-testid={`flag-watermark-${code}`}
    className={`fi fi-${code} pointer-events-none absolute block ${className}`}
    style={{
      width, height, opacity,
      backgroundSize: "cover",
      backgroundPosition: "center",
      lineHeight: 0,
      maskImage: mask,
      WebkitMaskImage: mask,
    }}
  />
);

// Country details — side panel on desktop, bottom sheet on phones/tablets.
const CountryPanel = ({ sel, onClose, sheet = false }) => {
  const { t } = useLang();
  const m = t.markets;
  const s = t.mapSheet;
  return (
    <div className="relative" data-testid={`map-panel-${sel.code}`}>
      <FlagWatermark
        code={sel.code}
        width="190px"
        height="126px"
        opacity={0.18}
        className="-right-5 -top-5"
        mask="radial-gradient(circle at 68% 32%, #000, transparent 70%)"
      />
      <div className="relative flex items-start justify-between gap-4">
        <h3 className="font-display flex items-center gap-3 text-2xl font-extrabold tracking-tight nv-head">
          <Flag code={sel.code} className="text-2xl" /> {m.names[sel.code] || sel.mapName}
        </h3>
        {sheet && (
          <button type="button" onClick={onClose} aria-label={s.close} data-testid="map-sheet-close" className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center text-[#41535F] hover:text-[#0F5C8C]">
            <X size={22} />
          </button>
        )}
      </div>
      {sel.hq && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#0F5C8C]"><MapPin size={12} /> {m.hq} · {CONTACT.addressLines[0]}</p>
      )}
      <div className="mt-4 border-t border-[#DDE4EA] pt-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#5E7280]">{s.services}</p>
        <ul className="mt-2 space-y-1.5 text-sm text-[#0E1B24]">
          <li className="flex items-center gap-2"><Check size={15} className="text-[#0F5C8C]" /> {s.hse}</li>
          <li className="flex items-center gap-2"><Check size={15} className="text-[#0F5C8C]" /> {s.manpower}</li>
        </ul>
      </div>
      <div className="mt-4 border-t border-[#DDE4EA] pt-4">
        <Link to={`/contact?country=${sel.code}#enquiry`} onClick={onClose} data-testid={`map-apply-${sel.code}`} className={`${btn} w-full bg-gradient-brand text-white active:scale-[0.98]`}>
          {s.enquire} <ArrowRight size={16} />
        </Link>
        <Link to="/contact#enquiry" onClick={onClose} data-testid={`map-contact-${sel.code}`} className={`${btn} mt-2 w-full border border-[#0E1B24]/70 text-[#0E1B24] hover:bg-[#0E1B24] hover:text-[#F4F6F8]`}>
          {s.contact}
        </Link>
      </div>
    </div>
  );
};

export const WorldMap = ({ index = "MAP", id, showHeader = true }) => {
  const { t, setLang } = useLang();
  const m = t.markets;
  const s = t.mapSheet;
  const isMobile = useIsMobile();
  const markets = useMarkets();
  const byName = useMemo(() => Object.fromEntries(markets.map((mk) => [mk.mapName, mk])), [markets]);
  const [selCode, setSelCode] = useState(null);
  const sel = markets.find((mk) => mk.code === selCode) || markets.find((mk) => mk.hq) || markets[0];
  const [sheet, setSheet] = useState(false);
  const [pos, setPos] = useState({ coordinates: CENTER, zoom: 1 });
  const zoomRef = useRef(1);
  zoomRef.current = pos.zoom;

  const pick = (mk) => {
    setSelCode(mk.code);
    // Switch to the country's preferred language (never overrides a manual choice).
    if (MAP_CODE_TO_LANG[mk.code]) setLang(MAP_CODE_TO_LANG[mk.code], { manual: false });
    if (isMobile) setSheet(true);
  };
  const zoomBy = (f) => setPos((p) => ({ ...p, zoom: clampZoom(p.zoom * f) }));
  const reset = () => setPos({ coordinates: CENTER, zoom: 1 });
  // Let the page scroll normally: only pinch (2 fingers) or drag-when-zoomed moves the map.
  const filterZoom = (e) => {
    if (e.type === "wheel") return false;
    if (e.touches && e.touches.length >= 2) return true;
    return zoomRef.current > 1;
  };

  useEffect(() => {
    if (!sheet) return undefined;
    const onKey = (e) => e.key === "Escape" && setSheet(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheet]);

  const zoomBtn = "flex h-11 w-11 items-center justify-center border border-[#C8D3DB] bg-[#F4F6F8]/90 text-[#0E1B24] backdrop-blur transition-colors hover:border-[#0F5C8C] disabled:opacity-40";

  return (
    <section id={id} className="mx-auto max-w-[1760px] scroll-mt-20 px-5 py-20 sm:px-8 sm:py-32 lg:scroll-mt-32" data-testid="world-map-section">
      <Chapter index={index} label={m.label} title={m.title} lead={m.lead} className={showHeader ? "" : "sr-only"} />
      <div className="mt-10 grid gap-6 lg:mt-12 lg:grid-cols-[2.1fr_1fr] lg:gap-8">
        <Reveal className="relative overflow-hidden border border-[#DDE4EA] glass p-1 sm:p-4" style={{ touchAction: pos.zoom > 1 ? "none" : "pan-y" }}>
          <AnimatePresence mode="wait">
            <motion.span
              key={sel.code}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="pointer-events-none absolute inset-0"
              aria-hidden="true"
            >
              <FlagWatermark
                code={sel.code}
                width="100%"
                height="100%"
                opacity={0.09}
                className="inset-0 blur-[2px]"
                mask="radial-gradient(ellipse 60% 55% at 50% 42%, #000 0%, transparent 78%)"
              />
            </motion.span>
          </AnimatePresence>
          <ComposableMap
            projection="geoAzimuthalEqualArea"
            projectionConfig={{ rotate: [-22, -44, 0], scale: 560 }}
            width={800}
            height={660}
            style={{ width: "100%", height: "auto" }}
            data-testid="world-map-svg"
          >
            <ZoomableGroup zoom={pos.zoom} center={pos.coordinates} minZoom={1} maxZoom={4} filterZoomEvent={filterZoom} onMoveEnd={(p) => setPos(p)}>
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const mk = byName[geo.properties.name];
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onClick={mk ? () => pick(mk) : undefined}
                        style={{
                          default: { fill: mk ? "#0F5C8C" : "#E4EAF0", stroke: "#FFFFFF", strokeWidth: 0.5, outline: "none", cursor: mk ? "pointer" : "default" },
                          hover: { fill: mk ? "#1477B3" : "#D6DEE6", outline: "none", cursor: mk ? "pointer" : "default" },
                          pressed: { fill: "#1477B3", outline: "none" },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
              {markets.map((mk) => {
                const isSel = sel?.code === mk.code;
                const poleH = isSel ? 32 : 26;
                const fw = isSel ? 28 : 22;
                const fh = isSel ? 19 : 15;
                return (
                  <Marker key={mk.code} coordinates={mk.coords} onClick={() => pick(mk)} style={{ default: { cursor: "pointer" }, hover: { cursor: "pointer" } }} data-testid={`map-marker-${mk.code}`}>
                    {/* larger transparent hit area for easy tapping */}
                    <circle r={18} fill="transparent" />
                    {/* base of the pole */}
                    {isSel && <circle r={5} fill="#0F5C8C" opacity={0.3} />}
                    <circle r={2.6} fill="#0F5C8C" stroke="#F4F6F8" strokeWidth={0.7} />
                    {/* flag pole */}
                    <rect x={-0.9} y={-poleH} width={1.8} height={poleH} rx={0.9} fill="#0E1B24" stroke="#F4F6F8" strokeWidth={0.3} />
                    {/* country flag at top of the pole */}
                    <foreignObject x={0.9} y={-poleH} width={fw} height={fh} style={{ overflow: "visible" }}>
                      <span
                        className={`fi fi-${mk.code}`}
                        style={{
                          display: "block",
                          width: `${fw}px`,
                          height: `${fh}px`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          border: "0.5px solid rgba(255,255,255,0.9)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.55)",
                        }}
                      />
                    </foreignObject>
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>
          <p className="pointer-events-none absolute left-3 top-3 border border-[#C8D3DB] bg-[#F4F6F8]/80 px-2.5 py-1.5 text-[11px] font-medium text-[#41535F] backdrop-blur lg:hidden" data-testid="map-hint">{s.hint}</p>
          <div className="absolute bottom-3 right-3 flex flex-col gap-1.5" data-testid="map-zoom-controls">
            <button type="button" onClick={() => zoomBy(1.6)} disabled={pos.zoom >= 4} aria-label={s.zoomIn} data-testid="map-zoom-in" className={zoomBtn}><Plus size={18} /></button>
            <button type="button" onClick={() => zoomBy(1 / 1.6)} disabled={pos.zoom <= 1} aria-label={s.zoomOut} data-testid="map-zoom-out" className={zoomBtn}><Minus size={18} /></button>
            <button type="button" onClick={reset} aria-label={s.reset} data-testid="map-zoom-reset" className={zoomBtn}><Maximize2 size={16} /></button>
          </div>
        </Reveal>

        <div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:flex lg:flex-wrap" data-testid="map-country-list">
            {markets.map((mk) => (
              <button
                key={mk.code}
                type="button"
                onClick={() => pick(mk)}
                data-testid={`map-country-${mk.code}`}
                aria-pressed={sel?.code === mk.code}
                className={`inline-flex min-h-[48px] items-center gap-2 border px-3 text-sm font-semibold transition-colors active:scale-[0.98] ${sel?.code === mk.code ? "border-[#0F5C8C] bg-[#0F5C8C]/10 text-[#0F5C8C]" : "border-[#C8D3DB] text-[#41535F] hover:border-[#0F5C8C]"}`}
              >
                <Flag code={mk.code} /> <span className="truncate">{m.names[mk.code] || mk.mapName}</span>
              </button>
            ))}
          </div>

          {!isMobile && (
            <AnimatePresence mode="wait">
              <motion.div key={sel.code} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} className="mt-6 border border-[#DDE4EA] glass p-7">
                <CountryPanel sel={sel} />
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {createPortal(
      <AnimatePresence>
        {isMobile && sheet && (
          <motion.div key="sheet-root" className="fixed inset-0 z-[70]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} data-testid="map-sheet">
            <button type="button" aria-label={s.close} onClick={() => setSheet(false)} className="absolute inset-0 bg-black/60" data-testid="map-sheet-backdrop" />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={m.names[sel.code] || sel.mapName}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.6 }}
              onDragEnd={(_, info) => (info.offset.y > 90 || info.velocity.y > 600) && setSheet(false)}
              className="absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-2xl border-t border-[#C8D3DB] bg-[#FFFFFF] px-5 pt-3 pb-safe shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.8)]"
            >
              <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#C8D3DB]" aria-hidden="true" />
              <div className="pb-6">
                <CountryPanel sel={sel} sheet onClose={() => setSheet(false)} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body)}
    </section>
  );
};

export default WorldMap;
